import React from "react"
import { Link } from "react-router-dom"
import Icon from "../components/Icon"

function Navigation() {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false)

  return (
    <nav className="w-full bg-white/95 backdrop-blur-sm border-b border-gray-200/50 sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            <img
              src="/logofinalrounded.png"
              alt="RecordLOGS logo"
              className="w-8 h-8 rounded-lg object-cover shadow-sm"
            />
            <span className="text-xl font-bold text-slate-800">RecordLOGS</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-6">
            <Link to="/" className="text-slate-600 hover:text-green-600 transition-colors">
              Home
            </Link>
            <Link to="/available-units" className="text-slate-600 hover:text-green-600 transition-colors">
              Search Units
            </Link>
            <Link to="/login" className="bg-green-500 text-white px-4 py-2 rounded-lg hover:bg-green-600 transition-colors">
              Sign In
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            className="md:hidden p-2 rounded-lg text-slate-600 hover:bg-slate-100 transition-colors"
            aria-label="Toggle menu"
          >
            <Icon name={isMenuOpen ? "x" : "list"} size="1.25rem" />
          </button>
        </div>

        {/* Mobile Navigation Menu */}
        {isMenuOpen && (
          <div className="md:hidden border-t border-gray-200 py-4">
            <div className="flex flex-col space-y-3">
              <Link 
                to="/" 
                className="px-3 py-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="house" className="inline mr-2" size="1rem" />
                Home
              </Link>
              <Link 
                to="/available-units" 
                className="px-3 py-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="search" className="inline mr-2" size="1rem" />
                Search Units
              </Link>
              <Link 
                to="/user-guide" 
                className="px-3 py-2 text-slate-600 hover:text-green-600 hover:bg-green-50 rounded-lg transition-colors"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="book" className="inline mr-2" size="1rem" />
                User Guide
              </Link>
              <Link 
                to="/login" 
                className="px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors text-center font-medium"
                onClick={() => setIsMenuOpen(false)}
              >
                <Icon name="box-arrow-in-right" className="inline mr-2" size="1rem" />
                Sign In
              </Link>
            </div>
          </div>
        )}
      </div>
    </nav>
  )
}

function HeroSection() {
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-slate-50 via-white to-green-50">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(34,197,94,0.1),transparent_50%),radial-gradient(circle_at_80%_80%,rgba(34,197,94,0.1),transparent_50%)]" />
      
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
        <div className="text-center">
          <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Icon name="shield-lock" size="1.5rem" className="text-green-600" />
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-slate-800 leading-tight mb-4">
            Privacy Policy
          </h1>
          <p className="text-lg text-slate-600 max-w-3xl mx-auto">
            Your privacy is important to us. Learn how we collect, use, and protect your personal information.
          </p>
          <div className="mt-6 inline-flex items-center gap-2 bg-green-50 border border-green-200 px-4 py-2 rounded-full text-green-700">
            <Icon name="calendar-event" className="text-green-600" />
            <span className="text-sm font-medium">Effective Date: September 13, 2025</span>
          </div>
        </div>
      </div>
    </section>
  )
}

function PolicyContent() {
  const sections = [
    {
      title: "1. Information We Collect",
      icon: "person-lines-fill",
      items: [
        "Personal Information: Name, phone number, email, government-issued ID, and account credentials.",
        "Financial Information: Wallet balance, payment history, transaction records.",
        "Usage Data: Logins, actions within modules (rent payments, group contributions, school records, vehicle logs).",
        "Device & Technical Data: IP address, browser type, device identifiers."
      ]
    },
    {
      title: "2. How We Use Your Information",
      icon: "gear",
      items: [
        "Provide and manage RL services (wallet, modules, roles).",
        "Process payments and generate reports.",
        "Verify identity and secure accounts.",
        "Communicate updates, notices, and support.",
        "Comply with legal obligations."
      ]
    },
    {
      title: "3. Sharing of Information",
      icon: "people",
      items: [
        "We do not sell your data.",
        "Shared only with service providers (payments, hosting, support).",
        "Shared with authorized managers/landlords/group admins for module-related records.",
        "Disclosed to legal authorities where required by law."
      ]
    },
    {
      title: "4. Data Security",
      icon: "shield-check",
      items: [
        "All transactions are encrypted.",
        "Two-factor authentication (2FA) is encouraged.",
        "Access is role-based and restricted to authorized users."
      ]
    },
    {
      title: "5. Data Retention",
      icon: "clock",
      items: [
        "We retain your information only as long as necessary to provide services and comply with legal obligations.",
        "You may request deletion of your account and associated data (subject to lawful requirements)."
      ]
    },
    {
      title: "6. Your Rights",
      icon: "person-check",
      items: [
        "Access your personal data.",
        "Correct or update inaccurate information.",
        "Request deletion of your account.",
        "Withdraw consent for non-essential communications."
      ]
    },
    {
      title: "7. Cookies & Tracking",
      icon: "globe",
      items: [
        "RL may use cookies and similar tools to enhance user experience, monitor performance, and improve services."
      ]
    },
    {
      title: "8. Changes to This Policy",
      icon: "arrow-repeat",
      items: [
        "We may update this Privacy Policy from time to time.",
        "Changes will be posted on the RL platform with a new effective date."
      ]
    },
    {
      title: "9. Contact Us",
      icon: "envelope",
      items: [
        "For questions or requests about your data, contact:",
        "Email: support@recordlogs.com"
      ]
    }
  ]

  return (
    <section className="py-16 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-3xl font-bold text-slate-800 mb-4">RecordLOGS Privacy Policy</h2>
        </div>
        
        <div className="space-y-12">
          {sections.map((section, index) => (
            <div key={index} className="bg-white rounded-2xl p-8 shadow-sm border border-slate-200">
              <div className="flex items-center gap-4 mb-8">
                <div className="w-12 h-12 bg-green-100 rounded-xl flex items-center justify-center">
                  <Icon name={section.icon} size="1.25rem" className="text-green-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-800">{section.title}</h3>
              </div>
              
              <div className="space-y-4">
                {section.items.map((item, itemIndex) => (
                  <div key={itemIndex} className="flex items-start gap-4">
                    <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center font-semibold text-xs mt-1">
                      •
                    </div>
                    <p className="text-slate-600 leading-relaxed flex-1">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}

function Footer() {
  return (
    <footer className="bg-slate-800 text-white py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <img
                src="/logofinalrounded.png"
                alt="RecordLOGS logo"
                className="w-8 h-8 rounded-lg object-cover shadow-sm"
              />
              <span className="text-xl font-bold">RecordLOGS</span>
            </div>
            <p className="text-slate-300 mb-4 max-w-md">
              Your trusted partner in rental management. We are committed to protecting your privacy and ensuring the security of your personal information.
            </p>
          </div>
        </div>
        
        <div className="border-t border-slate-700 mt-8 pt-8 text-center text-slate-400">
          <p>© {new Date().getFullYear()} RecordLOGS. All rights reserved.</p>
        </div>
      </div>
    </footer>
  )
}

function PrivacyPolicyPage() {
  return (
    <div className="min-h-screen bg-white">
      <Navigation />
      <main>
        <HeroSection />
        <PolicyContent />
      </main>
      <Footer />
    </div>
  )
}

export default PrivacyPolicyPage

